import React, { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import MessageButton from "../reusable/MessageButton";
import PortfolioPreviewLightbox from "./PortfolioPreviewLightbox";
import { buildDisplayName } from "../../utils/nameFormatting";
import "../../styles/components/browse_photographers_page/PhotographerCard.css";

const availabilityLabels = {
  full: "Available",
  partial: "Limited availability",
  unavailable: "Unavailable",
};

export default function PhotographerCard({
  photographer,
  onHoverIn,
  onHoverOut,
  onSelect,
  isSelected = false,
}) {
  const { userAttributes } = useAuth();
  const [coverFailed, setCoverFailed] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const specialities = photographer.specialities
    ?.map(({ speciality }) => speciality)
    .filter(Boolean)
    .slice(0, 3) || [];

  const location = [photographer.city, photographer.state]
    .filter(Boolean)
    .filter((value, index, array) => array.indexOf(value) === index)
    .join(", ");

  const availabilityStatus = photographer.availability?.status;
  const availabilityLabel = availabilityLabels[availabilityStatus];
  const hasCoverPhoto = Boolean(photographer.cover_photo) && !coverFailed;
  const photographerName = buildDisplayName(
    photographer.given_name,
    photographer.family_name
  );
  const canMessage = Boolean(photographer.sub) && photographer.sub !== userAttributes?.sub;

  const selectProps = onSelect
    ? {
        role: "button",
        tabIndex: 0,
        "aria-label": `Select ${photographerName} on the map`,
        onClick: () => onSelect(photographer.sub),
        onKeyDown: (event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onSelect(photographer.sub);
          }
        },
      }
    : {};

  const openPreview = (event) => {
    event?.stopPropagation();
    setPreviewOpen(true);
  };

  return (
    <>
      <article
        className={`photographer-card${isSelected ? " photographer-card--selected" : ""}${hasCoverPhoto ? " photographer-card--has-cover" : ""}`}
        onMouseEnter={() => onHoverIn?.(photographer.sub)}
        onMouseLeave={onHoverOut}
      >
        <button
          type="button"
          className="photographer-card__media"
          onClick={openPreview}
          aria-label={`Preview ${photographerName || "photographer"} portfolio`}
        >
          {hasCoverPhoto && (
            <img
              src={photographer.cover_photo}
              onError={() => setCoverFailed(true)}
              className="photographer-card__cover-photo"
              alt=""
              aria-hidden="true"
            />
          )}
          <img
            src={photographer.profile_picture || "/images/default_profile_picture.png"}
            onError={(event) => {
              event.currentTarget.src = "/images/default_profile_picture.png";
            }}
            className="profile-picture"
            alt={photographerName}
          />
          <span className="photographer-card__preview-overlay" aria-hidden="true">
            Preview portfolio
          </span>
        </button>

        <div className="photographer-card__select-area" {...selectProps}>
          <div className="photographer-info">
            <h3>{photographerName}</h3>
            {location && <p className="photographer-location">{location}</p>}

            {availabilityLabel && (
              <p className={`photographer-availability photographer-availability--${availabilityStatus}`}>
                {availabilityLabel}
              </p>
            )}

            {specialities.length > 0 && (
              <div className="photographer-specialities" aria-label="Specialities">
                {specialities.map((speciality) => (
                  <span key={speciality}>{speciality}</span>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="photographer-card__actions">
          <div className="photographer-card__primary-actions">
            {photographer.url_slug && (
              <a
                className="photographer-card__profile-link"
                href={`/profile/${photographer.url_slug}`}
              >
                View profile
              </a>
            )}
            {canMessage && (
              <MessageButton
                className="photographer-card__message-action"
                userSub={photographer.sub}
                recipientName={photographerName}
              />
            )}
          </div>

          <button
            type="button"
            className="photographer-card__preview-action"
            onClick={openPreview}
          >
            Preview portfolio
          </button>
        </div>
      </article>

      <PortfolioPreviewLightbox
        photographer={{
          ...photographer,
          cover_photo: hasCoverPhoto ? photographer.cover_photo : null,
        }}
        isOpen={previewOpen}
        onClose={() => setPreviewOpen(false)}
      />
    </>
  );
}
